import { Component, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIf } from '@angular/common';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatError } from '@angular/material/select';
import {TranslateModule, TranslateService} from '@ngx-translate/core';

import { ButtonComponent } from '../../shared/button/button.component';
import { UploadFormComponent } from '../../shared/upload-form/upload-form.component';
import { InputComponent } from '../../shared/input/input.component';
import { ParsingLoadingModalComponent } from '../parsing-loading-modal/parsing-loading-modal.component';
import { CvResultModalComponent } from '../cv-result-modal/cv-result-modal.component';
import { RegistrationPageService } from '../../pages/registration-pages/registration-page/registration-page.service';

import { passwordStrengthValidator } from '../../shared/validators/validators';
import {
  ButtonSize,
  ButtonState,
  ButtonType,
  InputSize,
  InputState,
  IUser,
  MaterialType,
} from '../../../assets/types/dtoTypes';
import {ShowPopupService} from "../../shared/services/show-popup.service";
import {ProfilePageService} from "../../pages/main-pages/user/profile-page/profile-page.service";

@Component({
  selector: 'app-add-new-candidate-modal',
  standalone: true,
  imports: [
    ButtonComponent,
    UploadFormComponent,
    ReactiveFormsModule,
    NgIf,
    InputComponent,
    TranslateModule,
    MatError,
  ],
  templateUrl: './add-new-candidate-modal.component.html',
  styleUrl: './add-new-candidate-modal.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class AddNewCandidateModalComponent {
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<AddNewCandidateModalComponent>);
  private fb = inject(FormBuilder);
  private popupService = inject(ShowPopupService);
  private registrationPageService = inject(RegistrationPageService);
  private profilePageService = inject(ProfilePageService);
  private translate = inject(TranslateService);

  step = 1;
  manualEntrySelected = false;
  loadingDialogRef: MatDialogRef<ParsingLoadingModalComponent> | null = null;

  userId!: string;
  token!: string;

  form: FormGroup = this.fb.group({
    firstname: new FormControl('', Validators.required),
    lastname: new FormControl('', Validators.required),
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, passwordStrengthValidator()]],
  });

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonState = ButtonState;
  protected readonly ButtonType = ButtonType;
  protected readonly MaterialType = MaterialType;
  protected readonly InputState = InputState;
  protected readonly InputSize = InputSize;

  // -------------------------------------------
  // MANUAL ENTRY FLOW
  // -------------------------------------------

  onManualEntry(): void {
    this.manualEntrySelected = true;

    const dialog = this.dialog.open(CvResultModalComponent, {
      disableClose: true,
      panelClass: 'cv-modal-panel',
      data: { source: 'add-new-candidate', userId: this.userId },
    });

    dialog.afterClosed().subscribe((parsedCV: Partial<IUser>) => {
      if (parsedCV) this.updateCandidate(parsedCV);
    });
  }

  // -------------------------------------------
  // FILE UPLOAD FLOW
  // -------------------------------------------

  sendUploadedFile(fileUrl: string): void {
    if (!this.userId) return;

    this.loadingDialogRef = this.dialog.open(ParsingLoadingModalComponent, {
      width: '100%',
      maxWidth: '600px',
      panelClass: 'cv-modal-panel',
      disableClose: true,
    });

    this.loadingDialogRef.afterOpened().subscribe(() => {
      this.registrationPageService.parseCV(fileUrl).subscribe({
        next: (parsedResult) => {
          this.loadingDialogRef?.close();
          this.openResultModal(parsedResult, fileUrl);
        },
        error: () => {
          this.loadingDialogRef?.close();
          this.popupService.showPopup({
            status: 'failure',
            translationKey: 'job_details.popup.application.generalError',
            isBackToLogin: false,
          });
        },
      });
    });
  }

  private openResultModal(parsedResult: Partial<IUser>, fileUrl: string): void {
    const dialog = this.dialog.open(CvResultModalComponent, {
      disableClose: true,
      panelClass: 'cv-modal-panel',
      data: { ...parsedResult, source: 'add-new-candidate', userId: this.userId },
    });

    dialog.afterClosed().subscribe((parsedCV: Partial<IUser>) => {
      this.loadingDialogRef?.close();
      if (parsedCV) {
        const candidateData = {
          cvUrl: fileUrl,
          motivation: parsedCV.motivation,
          education: parsedCV.education,
          workExperience: parsedCV.workExperience,
          languages: parsedCV.languages,
          country: parsedCV.location,
          profileImageUrl: parsedCV.profileImageUrl,
        };

        this.saveCandidateWithMaterial(candidateData);
      }
    });
  }

  private saveCandidateWithMaterial(candidateData: any): void {
    this.registrationPageService.createNewMaterial({
      url: candidateData.cvUrl,
      userId: this.userId,
      type: MaterialType.CV,
    }).subscribe(() => {
      this.updateCandidate(candidateData);
    });
  }

  private updateCandidate(candidateData: Partial<IUser>): void {
    this.registrationPageService.updateUser(this.userId, candidateData).subscribe({
      next: () => {
        this.profilePageService.getProfileInfos(this.userId).subscribe((fullUser: IUser) => {
          this.dialogRef.close(fullUser);
        });
        this.popupService.showSuccessPopup({
          title: this.translate.instant('candidates.candidateUpdateSuccess'),
        });
      },
      error: (error) => {
        console.error('Candidate update failed:', error);
      },
    });
  }

  // -------------------------------------------
  // FORM VALIDATION
  // -------------------------------------------

  isInvalidAndTouched(field: string): boolean {
    const formField = this.form.get(field);
    return !!formField && formField.touched && formField.invalid;
  }

  // -------------------------------------------
  // FORM SUBMISSION
  // -------------------------------------------

  onSubmitRegistration(): void {
    if (this.form.invalid) return;

    const { email, password, firstname, lastname } = this.form.value;
    const user: Partial<IUser> = { email, firstname, password, lastname };

    this.registrationPageService.registerUserWithAgency(user).subscribe({
      next: (res) => {
        this.userId = res.userId;
        this.step = 2;
      },
      error: (err) => this.handleRegistrationError(err),
    });
  }

  private handleRegistrationError(err: any): void {
    const message = err?.error?.message;

    switch (message) {
      case 'VERIFICATION_PENDING':
        this.step = 2;
        break;
      case 'EMAIL_TAKEN':
        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.emailAlreadyRegistered',
          isBackToLogin: true,
        });
        break;
      default:
        this.popupService.showPopup({
          status: 'failure',
          translationKey: 'job_details.popup.application.generalError',
          isBackToLogin: false,
        });
        break;
    }
  }
}
